import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandMark from "@/components/BrandMark";
import { track } from "@/lib/analytics";
import { AlertTriangle, LogIn, UserPlus } from "lucide-react";

/**
 * Invitation acceptance page.
 *
 * Handles four cases:
 * - Unknown/revoked/expired/used token → error card.
 * - New user (no account for invited email) → collect name + password → create.
 * - Existing user, not signed in → collect password → sign in + attach.
 * - Signed in as the invited email → one-click accept.
 * - Signed in as a DIFFERENT email → refuse, offer sign-out or create new account.
 */
export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user, refreshMe, logout } = useAuth();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState(null); // structured error {code, invited_email, ...}
  const [loading, setLoading] = useState(true);

  // Form fields
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("new"); // new | existing

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/invitations/lookup/${token}`);
        if (cancelled) return;
        setInvite(data);
        // Check whether this email already has an account, to select mode.
        // We can't safely tell from public endpoint — infer from status codes on submit.
      } catch (e) {
        if (cancelled) return;
        setErr(formatApiErr(e.response?.data?.detail) || "This invitation link is invalid or expired.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Proactively detect wrong-account signed-in state as soon as we know both
  // the invitation email AND the current user email. This avoids one wasted
  // POST + 409 round-trip, so the UI never renders the join form for the wrong user.
  useEffect(() => {
    if (!invite || !user) return;
    const invitedEmail = (invite.email || "").toLowerCase();
    const meEmail = (user.email || "").toLowerCase();
    if (invitedEmail && meEmail && invitedEmail !== meEmail) {
      setNotice({ code: "wrong_account", invited_email: invitedEmail, signed_in_email: user.email });
    }
  }, [invite, user]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;                    // guard against double-submit
    setErr("");
    setBusy(true);
    try {
      const body = { token };
      if (mode === "new") { body.name = name; body.password = password; }
      else                { body.password = password; }
      await api.post("/invitations/accept", body);
      track("invitation_accepted");
      await refreshMe();
      // Set active org so AppShell renders the invited workspace.
      try {
        const { setActiveOrgId } = await import("@/lib/api");
        setActiveOrgId(invite.organization?.id);
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.debug("[accept-invite] setActiveOrgId failed", err);
      }
      nav("/app/office");
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "wrong_account") {
        setNotice(detail);
      } else if (e2.response?.status === 401) {
        setErr("That password is incorrect. If you do not have an account yet, use the New account tab.");
      } else if (e2.response?.status === 400) {
        // Existing account requires password; new account requires name + password.
        setErr(formatApiErr(detail) || "Please fill in the required fields.");
      } else {
        setErr(formatApiErr(detail) || "Could not accept invitation.");
      }
    } finally {
      setBusy(false);
    }
  };

  // Auto-accept once the user is signed in as the correct email.
  // Uses a ref-guard instead of a `busy`/`autoAccepted` state dependency so the
  // effect only re-fires when its two real triggers change (invite + user id).
  const autoAcceptedRef = useRef(false);
  useEffect(() => {
    if (!invite || !user) return;
    if (autoAcceptedRef.current) return;
    if (user.email?.toLowerCase() !== (invite.email || "").toLowerCase()) return;
    autoAcceptedRef.current = true;
    (async () => {
      setBusy(true);
      try {
        await api.post("/invitations/accept", { token });
        track("invitation_accepted");
        await refreshMe();
        const { setActiveOrgId } = await import("@/lib/api");
        setActiveOrgId(invite.organization?.id);
        nav("/app/office");
      } catch (e2) {
        const detail = e2.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.code === "wrong_account") setNotice(detail);
        else if (e2.response?.status !== 410) {
          // 410 usually means the invitation was already accepted by this same
          // session (double-fire during React strict-mode) — don't shout at the user.
          setErr(formatApiErr(detail) || "Could not accept invitation.");
        }
      } finally {
        setBusy(false);
      }
    })();
  }, [invite, user, token, refreshMe, nav]);

  if (loading) {
    return <Screen><div className="text-sm text-zinc-500">Loading invitation…</div></Screen>;
  }
  if (err && !invite) {
    return (
      <Screen>
        <div className="text-center max-w-md">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-semibold">This invitation isn&apos;t available</h1>
          <p className="text-sm text-zinc-600 mt-2">{err}</p>
          <Link to="/" className="mt-6 inline-flex text-sm text-[var(--brand-accent)]">Go to TOVRA →</Link>
        </div>
      </Screen>
    );
  }

  const workspace = invite?.organization?.name || "the workspace";
  const inviter = invite?.inviter_name || "A teammate";

  return (
    <Screen>
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><BrandMark theme="light" /></div>

        {notice?.code === "wrong_account" ? (
          <div className="rounded-xl border p-6 bg-white">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold">Signed in as a different account</h2>
                <p className="text-sm text-zinc-600 mt-2">
                  This invitation is for <b>{notice.invited_email}</b>, but you are signed in as <b>{notice.signed_in_email}</b>.
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  <Button data-testid="wrong-account-signout" onClick={async () => { await logout(); setNotice(null); }} className="bg-[var(--brand-primary)] text-white">
                    Sign out and continue
                  </Button>
                  <Link to="/app/office" className="text-xs text-zinc-500 hover:text-zinc-900 text-center">Cancel</Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border p-6 bg-white">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Invitation</div>
            <h1 className="text-2xl font-semibold" data-testid="invite-headline">
              Join <span style={{ color: "var(--brand-accent)" }}>{workspace}</span>
            </h1>
            <p className="text-sm text-zinc-600 mt-2">
              <b>{inviter}</b> invited <b>{invite.email}</b> to join {workspace} on TOVRA
              {invite.role ? <> as <b>{invite.role}</b></> : null}.
            </p>
            {invite.personal_message && (
              <div className="mt-3 text-sm rounded-md bg-zinc-50 border border-zinc-200 p-3 text-zinc-700">
                &ldquo;{invite.personal_message}&rdquo;
              </div>
            )}

            <div className="mt-6 flex gap-2 border-b -mx-6 px-6">
              <TabButton active={mode === "new"} onClick={() => setMode("new")} icon={UserPlus} testid="accept-tab-new">New account</TabButton>
              <TabButton active={mode === "existing"} onClick={() => setMode("existing")} icon={LogIn} testid="accept-tab-existing">Existing account</TabButton>
            </div>

            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="accept-form">
              {mode === "new" && (
                <div>
                  <Label>Your name</Label>
                  <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="accept-name" placeholder="Your full name" />
                </div>
              )}
              <div>
                <Label>{mode === "new" ? "Create a password" : "Your password"}</Label>
                <Input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="accept-password" />
              </div>
              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" role="alert">{err}</div>}
              <Button type="submit" disabled={busy} className="w-full h-11 bg-[var(--brand-primary)] text-white" data-testid="accept-submit">
                {busy ? "Joining…" : "Join workspace"}
              </Button>
            </form>

            <div className="mt-4 text-xs text-zinc-500 text-center">
              This invitation expires {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : "in 7 days"}.
            </div>
          </div>
        )}
      </div>
    </Screen>
  );
}

function Screen({ children }) {
  return (
    <div className="min-h-screen bg-[var(--bg-app)] flex items-center justify-center p-6">
      {children}
    </div>
  );
}
function TabButton({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 text-sm font-medium py-2.5 border-b-2 -mb-px transition ${
        active ? "border-[var(--brand-primary)] text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-800"
      }`}
    >
      <Icon className="w-4 h-4" />
      {children}
    </button>
  );
}
